import React from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import DatePicker from 'react-datepicker';
import { Icon } from 'semantic-ui-react';
import styled from 'styled-components';

import { CustomInput } from '@/components/utils/DatePicker/CustomInput';
import { INTERVAL_BUSINESS_DAYS, REMOTEST_MONTHS } from '@/constants';
import { Label } from '@/components/shared/Label';
import { ServiceEndingAndStartAnnouncement } from '@/components/pages/Subscription/Container/ServiceEndingAndStartAnnouncement';

export type Props = {
  minDate: Date;
  maxDate: Date;
  dateFormat: string;
  error?: string;
  startsOn: Date;
  setStartsOn: (date: Date) => void;
  serviceGroupId: number;
};

export const StartsOnView: React.FC<Props> = ({
  minDate,
  maxDate,
  startsOn,
  dateFormat,
  error,
  setStartsOn,
  serviceGroupId,
}) => {
  const { t } = useTranslation();
  return (
    <Styled>
      <div className="l-block">
        <div id="startsOn" className={classnames('field', { error })}>
          <label>{t('models.subscription.startsOn')}</label>
          <div className="date-picker">
            <DatePicker
              {...{ dateFormat, maxDate, minDate }}
              customInput={<CustomInput />}
              onChange={(date) => date instanceof Date && setStartsOn(date)}
              selected={startsOn}
            />
            <Icon name="calendar outline" className="calendar-icon" />
            {error && (
              <div>
                <Label>{error}</Label>
              </div>
            )}
            <div className="hint starts-on">
              <Icon name="idea" />
              <div>
                <div>
                  <span>
                    {t('ui.expression.afterBusinessDays', {
                      num: INTERVAL_BUSINESS_DAYS + 1,
                    })}
                    &nbsp;
                    {t('ui.expression.inputableMonthRange', {
                      num: REMOTEST_MONTHS,
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <ServiceEndingAndStartAnnouncement serviceGroupId={serviceGroupId} />
        </div>
      </div>
    </Styled>
  );
};

const Styled = styled.div`
  .l-block {
    margin-bottom: 3em;
  }

  .react-datepicker-wrapper .react-datepicker__input-container input {
    position: relative;
    display: inline-flex;
    width: 100%;
    max-width: 100%;
    flex: 1 0 auto;
    padding: 0.67857143em 1em;
    border: 1px solid rgba(34, 36, 38, 0.15);
    margin: 0;
    background: #fff;
    border-radius: 0.28571429rem;
    box-shadow: none;
    color: rgba(0, 0, 0, 0.87);
    font-style: normal;
    font-weight: 400;
    line-height: 1.21428571em;
    text-align: left;
    transition: box-shadow 0.1s ease, border-color 0.1s ease;
    transition-delay: initial, initial, initial;
    transition-duration: 0.1s, 0.1s, 0.1s;
    transition-timing-function: ease, ease, ease;
  }

  .date-picker {
    position: relative;
    width: 100%;
  }

  .date-picker > div {
    width: 49%;
  }

  .react-datepicker-wrapper {
    position: relative;
    width: 100%;
  }

  .react-datepicker__input-container {
    width: 100%;
  }

  .calendar-icon {
    position: absolute;
    top: 0.63em;
    left: 44%;
    pointer-events: none;
  }

  .hint.starts-on {
    width: 100%;
    display: flex;
  }

  /* calender */
  .react-datepicker__day--sat {
    color: #5281ff;
  }

  .react-datepicker__day--sun {
    color: #c74444;
  }

  .react-datepicker__day--disabled.react-datepicker__day--sat,
  .react-datepicker__day--disabled.react-datepicker__day--sun {
    opacity: 0.3;
  }
  .react-datepicker__day--selected.react-datepicker__day--sat,
  .react-datepicker__day--selected.react-datepicker__day--sun {
    color: #fff !important;
  }
`;
